'use client';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'motion/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/** Where the reveal finishes — kept in sync with the `useScroll` end offset below. */
const SETTLE_POINT = 0.6;

/**
 * Decided once, after first paint:
 *
 * - `pending` — not measured yet (SSR and first client paint). Renders plain.
 * - `static`  — the element is already past the reveal point on load. Scroll-linked
 *               progress initialises at 0 and only resolves once Motion measures the
 *               target, so animating here springs the element in from a tilted,
 *               half-transparent state with no scroll available to drive it. Above the
 *               fold that unwanted flash plays on every visit, which reads as a broken
 *               image rather than as a reveal.
 * - `scroll`  — below the fold, so the reveal has room to play properly.
 */
function useRevealMode(ref: React.RefObject<HTMLDivElement | null>) {
  const [mode, setMode] = useState<'pending' | 'static' | 'scroll'>('pending');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Measured before images load, so height is often 0 here — that only biases the
    // center upwards, which correctly resolves to `static` for on-screen elements.
    const alreadySettled = rect.top + rect.height / 2 < window.innerHeight * SETTLE_POINT;
    setMode(alreadySettled ? 'static' : 'scroll');
  }, [ref]);

  return mode;
}

/**
 * Scroll-linked 3D straightening (the lusispro ContainerScroll essence, tamed
 * for an editorial page): the element starts tilted back in perspective and
 * rotates upright / scales to full size as it scrolls into view, smoothed
 * with springs. Purely scroll-driven — scrubbing back re-tilts it.
 *
 * Only applies to elements that enter from below the fold; anything already on
 * screen at load renders settled. See `useRevealMode`.
 */
export function TiltReveal({
  children,
  maxTilt = 16,
  className,
}: {
  children: ReactNode;
  maxTilt?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mode = useRevealMode(ref);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', `center ${SETTLE_POINT}`] });
  const spring = { stiffness: 120, damping: 22 };
  const rotateX = useSpring(useTransform(scrollYProgress, [0, 1], [maxTilt, 0]), spring);
  const scale = useSpring(useTransform(scrollYProgress, [0, 1], [0.92, 1]), spring);
  const opacity = useSpring(useTransform(scrollYProgress, [0, SETTLE_POINT], [0.4, 1]), spring);

  if (mode !== 'scroll' || reduce) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} className={className} style={{ perspective: '1200px' }}>
      <motion.div style={{ rotateX, scale, opacity, transformStyle: 'preserve-3d' }}>{children}</motion.div>
    </div>
  );
}
